import type { Patch } from "./types";
import { Voice } from "./Voice";

export function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

// The reusable, DOM-free synth engine. Owns the AudioContext and output chain
// (voices -> master -> analyser -> speakers) and manages polyphonic voices.
export class Synth {
  readonly ctx: AudioContext;
  readonly analyser: AnalyserNode;
  private readonly master: GainNode;
  private readonly voices = new Map<number, Voice>();
  patch: Patch;

  constructor(patch: Patch) {
    this.patch = patch;
    this.ctx = new AudioContext();
    this.master = new GainNode(this.ctx, { gain: patch.masterGain });
    this.analyser = new AnalyserNode(this.ctx, { fftSize: 2048 });
    this.master.connect(this.analyser);
    this.analyser.connect(this.ctx.destination);
  }

  /** Browsers require a user gesture before audio can start. */
  async resume(): Promise<void> {
    if (this.ctx.state !== "running") await this.ctx.resume();
  }

  noteOn(midi: number): void {
    void this.resume();
    this.voices.get(midi)?.stop(this.ctx.currentTime);
    const voice = new Voice(this.ctx, this.patch, this.master);
    voice.start(this.ctx.currentTime, midiToFreq(midi), this.patch.ampEnv);
    this.voices.set(midi, voice);
  }

  noteOff(midi: number): void {
    const voice = this.voices.get(midi);
    if (!voice) return;
    voice.stop(this.ctx.currentTime);
    this.voices.delete(midi);
  }

  // Replace the active patch. Most settings take effect on the next note;
  // master gain and filter cutoff are applied live to sounding voices.
  setPatch(patch: Patch): void {
    this.patch = patch;
    this.master.gain.setTargetAtTime(patch.masterGain, this.ctx.currentTime, 0.01);
    for (const voice of this.voices.values()) {
      voice.setFilterCutoff(patch.filter.cutoff);
    }
  }

  /** Schedule one note of a patch at an absolute time, for a given duration. */
  scheduleNote(patch: Patch, midi: number, when: number, dur: number): void {
    const voice = new Voice(this.ctx, patch, this.master);
    voice.start(when, midiToFreq(midi), patch.ampEnv);
    voice.stop(when + Math.max(0.05, dur));
  }

  /** Play a one-shot preview of a patch at a given note (used by the palette). */
  previewPatch(patch: Patch, midi: number): void {
    void this.resume();
    this.scheduleNote(patch, midi, this.ctx.currentTime, 0.7);
  }
}
